import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useGetList, useNotify } from 'react-admin';
import { tokens, useBrsCreateStyles } from '../BrsCreateStyles';

// Destinations entry matching the legacy DestinationListInput / AddonDialog UX:
//   - LEFT: only the selected destinations show as removable chips
//   - RIGHT: free-form comma/newline-separated textarea with Add / Remove
//   - TOP-LEFT: autocomplete that searches the full catalog (the ONLY place
//     the full destination list is visible)
// Selecting from the autocomplete or typing call letters in the textarea adds
// to selected. Removing happens via the chip × or by typing the call letters
// into the textarea and clicking Remove.
export const DestinationsSection = ({
  selectedDestinations,
  onDestinationsChange,
  onAntennaSDChange,
}) => {
  const classes = useBrsCreateStyles();
  const notify = useNotify();
  const [searchText, setSearchText] = useState('');
  const [textareaValue, setTextareaValue] = useState('');
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const autocompleteRef = useRef(null);

  const { data, loading } = useGetList(
    'destination',
    { page: 1, perPage: 10000 },
    { field: 'call_letters', order: 'ASC' },
  );

  const allDestinations = useMemo(
    () =>
      Object.values(data || {}).map((d) => ({
        id: d.id,
        name: d.name,
        call_letters: d.call_letters,
      })),
    [data],
  );

  const selectedIds = useMemo(
    () => new Set(selectedDestinations.map((d) => d.id)),
    [selectedDestinations],
  );

  // Autocomplete results: full catalog minus already-selected, filtered by
  // searchText. Capped at 50 to keep the popover snappy.
  const autocompleteResults = useMemo(() => {
    const lower = searchText.trim().toLowerCase();
    const available = allDestinations.filter((d) => !selectedIds.has(d.id));
    if (!lower) return available.slice(0, 50);
    return available
      .filter(
        (d) =>
          d.name.toLowerCase().includes(lower) ||
          d.call_letters.toLowerCase().includes(lower),
      )
      .slice(0, 50);
  }, [allDestinations, selectedIds, searchText]);

  // Auto-toggle Antenna-SD when any selected destination matches "antenna".
  useEffect(() => {
    const hasAntenna = selectedDestinations.some(
      (d) =>
        d.name?.toLowerCase().includes('antenna') ||
        d.call_letters?.toLowerCase().includes('antenna'),
    );
    onAntennaSDChange(hasAntenna);
  }, [selectedDestinations, onAntennaSDChange]);

  // Close the autocomplete popover when clicking outside it.
  useEffect(() => {
    if (!autocompleteOpen) return undefined;
    const handleClickOutside = (event) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(event.target)) {
        setAutocompleteOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [autocompleteOpen]);

  const handleAddFromAutocomplete = (dest) => {
    if (selectedIds.has(dest.id)) return;
    onDestinationsChange([...selectedDestinations, dest]);
    setSearchText('');
    setAutocompleteOpen(false);
  };

  const handleRemove = (destId) => {
    onDestinationsChange(selectedDestinations.filter((d) => d.id !== destId));
  };

  const parseTextareaTokens = () =>
    textareaValue
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter(Boolean);

  const handleAddFromTextarea = () => {
    const tokens = parseTextareaTokens();
    if (tokens.length === 0) return;
    const byCallLetters = new Map(
      allDestinations.map((d) => [d.call_letters.toLowerCase(), d]),
    );
    const matched = [];
    const invalid = [];
    const already = [];
    const seenInBatch = new Set();
    for (const t of tokens) {
      const key = t.toLowerCase();
      if (seenInBatch.has(key)) continue;
      seenInBatch.add(key);
      const dest = byCallLetters.get(key);
      if (!dest) {
        invalid.push(t);
        continue;
      }
      if (selectedIds.has(dest.id)) {
        already.push(dest.call_letters);
        continue;
      }
      matched.push(dest);
    }
    if (matched.length > 0) {
      onDestinationsChange([...selectedDestinations, ...matched]);
    }
    if (invalid.length > 0) {
      notify(`Unknown destination${invalid.length === 1 ? '' : 's'}: ${invalid.join(', ')}`, 'warning');
    }
    if (already.length > 0) {
      notify(`Already added: ${already.join(', ')}`, 'info');
    }
    setTextareaValue('');
  };

  const handleRemoveFromTextarea = () => {
    const tokens = parseTextareaTokens().map((t) => t.toLowerCase());
    if (tokens.length === 0) return;
    onDestinationsChange(
      selectedDestinations.filter(
        (d) => !tokens.includes((d.call_letters || '').toLowerCase()),
      ),
    );
    setTextareaValue('');
  };

  return (
    <>
      <div className={classes.sectionHeader} style={{ marginTop: 20 }}>
        Destinations
      </div>
      <div className={classes.card}>
        <div className={classes.formRow}>
          <div style={{ flex: '0 0 50%', minWidth: 320 }}>
            <div ref={autocompleteRef} style={{ position: 'relative' }}>
              <input
                className={classes.destSearch}
                placeholder="Search destinations to add..."
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setAutocompleteOpen(true);
                }}
                onFocus={() => setAutocompleteOpen(true)}
              />
              {autocompleteOpen && (
                <div className={classes.destAutocompleteList}>
                  {loading ? (
                    <div className={classes.destAutocompleteEmpty}>Loading destinations…</div>
                  ) : autocompleteResults.length === 0 ? (
                    <div className={classes.destAutocompleteEmpty}>No matches</div>
                  ) : (
                    autocompleteResults.map((dest) => (
                      <div
                        key={dest.id}
                        className={classes.destAutocompleteItem}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleAddFromAutocomplete(dest);
                        }}>
                        <span className={classes.destAutocompleteCall}>{dest.call_letters}</span>
                        <span className={classes.destAutocompleteName}>{dest.name}</span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            <div className={classes.destChips} style={{ marginTop: 12 }}>
              {selectedDestinations.length === 0 ? (
                <span style={{ fontSize: 12, color: tokens.textDim }}>
                  No destinations selected. Search above or add comma-separated call letters on the right.
                </span>
              ) : (
                selectedDestinations.map((dest) => {
                  const isAntenna =
                    dest.name?.toLowerCase().includes('antenna') ||
                    dest.call_letters?.toLowerCase().includes('antenna');
                  return (
                    <div
                      key={dest.id}
                      className={`${classes.destChip} ${classes.destChipSelected}`}
                      style={
                        isAntenna
                          ? {
                              background: 'rgba(255,179,64,0.1)',
                              borderColor: tokens.warning,
                              color: tokens.warning,
                            }
                          : {}
                      }>
                      {dest.call_letters}
                      <span
                        className={classes.destChipClose}
                        onClick={() => handleRemove(dest.id)}>
                        &times;
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          <div style={{ flex: '0 0 45%', minWidth: 280 }}>
            <div className={classes.destIdsLabel}>Destination IDs</div>
            <textarea
              className={classes.formInput}
              style={{ minHeight: 120, resize: 'vertical' }}
              placeholder="Enter comma-separated call letters (e.g. WABC, WXYZ)"
              value={textareaValue}
              onChange={(e) => setTextareaValue(e.target.value)}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
              <button
                type="button"
                className={`${classes.btn} ${classes.btnGhost}`}
                onClick={handleRemoveFromTextarea}
                disabled={!textareaValue.trim()}>
                Remove
              </button>
              <button
                type="button"
                className={`${classes.btn} ${classes.btnPrimary}`}
                onClick={handleAddFromTextarea}
                disabled={!textareaValue.trim()}>
                Add
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
